import React, { Dispatch, ReactElement, SetStateAction, useEffect } from 'react';
import { FormField, Grid, Input, Spinner } from '@amzn/awsui-components-react-v3';
import Icon from '@amzn/awsui-components-react/polaris/icon';
import { useLazyQuery } from '@apollo/client';
import { ContainerValidated, GetContainerResponse } from '../../containers/PartsMove/types';
import {
  GET_CMS_CONTAINER,
  GET_CMS_CONTAINER_BY_ASSET_TAG,
} from '../../services/container-management/queries';
import { GET_BIN, GET_PALLET, GET_TOTE } from '../../services/parts-move/queries';
import { ParentContainersMobility } from '../../services/parts-move/types';

export interface ValidatingContainerInputProps {
  containerId: string;
  setContainerId: Dispatch<SetStateAction<string>>;
  label: string;
  disabled: boolean;
  validationStatus: ContainerValidated;
  setValidation: Dispatch<SetStateAction<ContainerValidated>>;
  setContainer: Dispatch<SetStateAction<GetContainerResponse | undefined>>;
  setMobilityId: Dispatch<SetStateAction<number>>;
  setParentMobilityIds: Dispatch<SetStateAction<ParentContainersMobility[]>>;
}

export default function ValidatingContainerInput(
  props: ValidatingContainerInputProps
): ReactElement {
  const { containerId } = props;
  const { setContainerId } = props;
  const { label } = props;
  const { disabled } = props;
  const { validationStatus } = props;
  const { setValidation } = props;
  const { setContainer } = props;
  const { setMobilityId } = props;
  const { setParentMobilityIds } = props;
  const [errorMessage, setErrorMessage] = React.useState('');
  const [parentContainers, setParentContainers] = React.useState<ParentContainersMobility[]>([]);

  const [getContainerByAssetTag, { data: cmsData, loading: cmsLoading, error: cmsError }] =
    useLazyQuery(GET_CMS_CONTAINER_BY_ASSET_TAG, {
      variables: { assetTag: '' },
      fetchPolicy: 'no-cache',
    });

  const [getBin] = useLazyQuery(GET_BIN, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    onCompleted: (result) => {
      setMobilityId(result.getBin?.id);
    },
    onError: (e) => {
      setMobilityId(-1);
    },
  });

  const [getTote] = useLazyQuery(GET_TOTE, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    onCompleted: (result) => {
      setMobilityId(result.getTote?.id);
    },
    onError: (e) => {
      setMobilityId(-1);
    },
  });

  const [getPallet] = useLazyQuery(GET_PALLET, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    onCompleted: (result) => {
      setMobilityId(result.getPallet?.id);
    },
    onError: (e) => {
      setMobilityId(-1);
    },
  });

  const [getParentContainer] = useLazyQuery(GET_CMS_CONTAINER, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    onCompleted: (result) => {
      const newContainer: ParentContainersMobility = {
        cmsId: result.getCmsContainer?.container.containerId,
        type: result.getCmsContainer?.containerDefinition.type,
        mobilityId: '',
      };
      setParentContainers([newContainer, ...parentContainers]);
    },
    onError: (e) => {
      setParentContainers([...parentContainers]);
    },
  });

  const [getParentBin] = useLazyQuery(GET_BIN, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    onCompleted: (result) => {
      parentContainers.forEach((container) => {
        if (container.cmsId === result.getBin?.cms_id) {
          // eslint-disable-next-line no-param-reassign
          container.mobilityId = result.getBin?.id;
        }
      });
      setParentContainers([...parentContainers]);
      setParentMobilityIds([...parentContainers]);
    },
  });

  const [getParentPallet] = useLazyQuery(GET_PALLET, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    onCompleted: (result) => {
      parentContainers.forEach((container) => {
        if (container.cmsId === result.getBin?.cms_id) {
          // eslint-disable-next-line no-param-reassign
          container.mobilityId = result.getBin?.id;
        }
      });
      setParentContainers([...parentContainers]);
      setParentMobilityIds([...parentContainers]);
    },
  });

  useEffect(() => {
    if (cmsLoading) {
      setValidation(ContainerValidated.LOADING);
    } else if (cmsError) {
      setValidation(ContainerValidated.ERROR);
      let error = cmsError.message;
      if (cmsError.message.includes('GraphQL error: ')) {
        error = cmsError.message.substr('GraphQL error: '.length, cmsError.message.length);
      }
      setErrorMessage(error);
    } else if (cmsData) {
      const response: GetContainerResponse = cmsData?.getCmsContainerByAssetTag;
      setContainer(cmsData?.getCmsContainerByAssetTag);
      setValidation(
        response?.container.isActive ? ContainerValidated.VALIDATED : ContainerValidated.INACTIVE
      );
      const getMobilityIdQuery = {
        variables: {
          containerId: cmsData?.getCmsContainerByAssetTag.container.containerId,
        },
      };
      switch (response.containerDefinition.containerType) {
        case 'bin':
          getBin(getMobilityIdQuery);
          break;
        case 'tote':
          getTote(getMobilityIdQuery);
          break;
        case 'pallet':
          getPallet(getMobilityIdQuery);
          break;
        default:
          getBin(getMobilityIdQuery);
          break;
      }
      const parents = cmsData?.getCmsContainerByAssetTag.container.parentlocation;
      const containers = parents.filter((container) => container.type === 'CONTAINER');
      containers.forEach((container) => {
        getParentContainer({
          variables: {
            containerId: container.value,
          },
        });
      });
    }
  }, [
    cmsData,
    cmsLoading,
    cmsError,
    setValidation,
    setContainer,
    getBin,
    getTote,
    getPallet,
    getParentContainer,
  ]);

  useEffect(() => {
    parentContainers.forEach((container) => {
      if (container.mobilityId === '') {
        if (container.type === 'BIN') {
          getParentBin({
            variables: {
              containerId: container.cmsId,
            },
          });
        } else if (container.type === 'PALLET') {
          getParentPallet({
            variables: {
              containerId: container.cmsId,
            },
          });
        }
      }
    });
  }, [getParentBin, getParentPallet, parentContainers]);

  const containerIdOnChange = (changedId: string): void => {
    setContainerId(changedId);
    setValidation(ContainerValidated.UNCHECKED);
    setErrorMessage('');
  };

  // create an input component that:
  // takes an input string
  // calls cms with that input string
  // shows a spinner while to call to cms is ocurring
  // shows a green success check when container is validated (possibly disables)
  // shows an error in the form field when the container is either not found or inactive
  return (
    <FormField label={label} errorText={errorMessage}>
      <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
        <Input
          value={containerId}
          disabled={disabled}
          onBlur={(): void => {
            if (containerId) getContainerByAssetTag({ variables: { assetTag: containerId } });
          }}
          onChange={(event): void => containerIdOnChange(event.detail.value)}
        />
        {cmsLoading && <Spinner />}
        {validationStatus === ContainerValidated.VALIDATED && (
          <Icon name="status-positive" variant="success" size="normal" />
        )}
      </Grid>
    </FormField>
  );
}
